import { DeckCard } from "modules/decks/model/deck";

interface DrawButton {
  count: number;
  label: string;
  shortcutKey: string;
  shortcutLabel: string;
}

export interface HandVerdict {
  label: string;
  className: string;
}

export interface ResourceStat {
  /** Label shown in the analytics row (e.g. "Lands", "Early plays") */
  label: string;
  /** Count how many cards in the hand match this resource */
  count: (hand: DeckCard[]) => number;
  /** Range [min, max] considered "good" - used for stat coloring */
  goodRange: [number, number];
}

export interface HypergeomPresetConfig {
  id: string;
  label: string;
  /** Count how many copies of the target exist in the mainboard pool */
  countK: (mainboard: DeckCard[]) => number;
  want: number;
  rel: "gte" | "eq" | "lte";
}

export interface SecondaryPoolConfig {
  /** Display label (e.g. "Runes") */
  label: string;
  /** Card types that belong to this pool */
  includeTypes: string[];
  /** How many to draw per turn */
  drawPerTurn: number;
}

export interface GameHandRules {
  handSize: number;
  mulliganType: "full" | "partial";
  maxMulligans: number | null;
  partialMulliganMaxReturn: number;
  drawButtons: DrawButton[];
  excludeTypes: string[];
  labels: {
    mulliganButton: string;
    shortcutHint: string;
    /** Label for the cost distribution chart (e.g. "Mana curve", "Cost curve") */
    costCurveTitle: string;
    /** Subtitle for non-resource spells (e.g. "Non-land spells only", "Action cards only") */
    costCurveSubtitle: string;
  };
  /** Evaluate the opening hand and return a verdict */
  getVerdict: (hand: DeckCard[]) => HandVerdict;
  /** Config for the resource stat shown in the analytics row */
  resourceStat: ResourceStat;
  /** Presets for the hypergeometric calculator */
  hypergeomPresets: HypergeomPresetConfig[];
  /** Secondary deck pool drawn alongside the main pool (e.g. Riftbound runes) */
  secondaryPool?: SecondaryPoolConfig;
  /** How many cards to draw from the main pool per turn (used by "Draw turn" button) */
  mainDrawPerTurn: number;
}

export const RIFTBOUND_NON_MAIN_TYPES = ["Legend", "Battlefield", "Rune"];

/* ── Helpers ─────────────────────────────────────────────────── */

function countByType(cards: DeckCard[], type: string): number {
  return cards.filter((c) => c.types.includes(type)).length;
}

function sumByType(cards: DeckCard[], type: string): number {
  return cards
    .filter((c) => c.types.includes(type))
    .reduce((s, c) => s + c.quantity, 0);
}

function countByCostAtMost(cards: DeckCard[], maxCost: number): number {
  return cards.filter((c) => c.costValue <= maxCost).length;
}

function sumByCostAtMost(cards: DeckCard[], maxCost: number): number {
  return cards
    .filter((c) => c.costValue <= maxCost)
    .reduce((s, c) => s + c.quantity, 0);
}

/* ── MTG ─────────────────────────────────────────────────────── */

export const MTG_HAND_RULES: GameHandRules = {
  handSize: 7,
  mulliganType: "full",
  maxMulligans: null,
  partialMulliganMaxReturn: 0,
  drawButtons: [
    { count: 1, label: "Draw 1", shortcutKey: " ", shortcutLabel: "Space" },
    { count: 3, label: "Draw 3", shortcutKey: "3", shortcutLabel: "3" },
    { count: 5, label: "Draw 5", shortcutKey: "5", shortcutLabel: "5" },
  ],
  excludeTypes: [],
  labels: {
    mulliganButton: "Mulligan",
    shortcutHint:
      "Click cards to remove \u2022 Space = draw \u2022 R = new hand \u2022 ESC = close",
    costCurveTitle: "Mana curve",
    costCurveSubtitle: "Non-land spells only.",
  },
  mainDrawPerTurn: 1,
  getVerdict: (hand) => {
    const lands = countByType(hand, "Land");
    if (lands < 2)
      return { label: "Risky \u2014 under 2 lands", className: "text-warning" };
    if (lands > 5)
      return {
        label: "Flooded \u2014 consider mulligan",
        className: "text-warning",
      };
    if (lands <= 4) return { label: "Keepable", className: "text-primary" };
    return {
      label: "Land-heavy but playable",
      className: "text-base-content/70",
    };
  },
  resourceStat: {
    label: "Lands",
    count: (hand) => countByType(hand, "Land"),
    goodRange: [2, 5],
  },
  hypergeomPresets: [
    {
      id: "land2",
      label: "At least 2 lands",
      countK: (mb) => sumByType(mb, "Land"),
      want: 2,
      rel: "gte",
    },
    {
      id: "land3",
      label: "At least 3 lands",
      countK: (mb) => sumByType(mb, "Land"),
      want: 3,
      rel: "gte",
    },
    {
      id: "no-land",
      label: "Zero lands",
      countK: (mb) => sumByType(mb, "Land"),
      want: 0,
      rel: "eq",
    },
  ],
};

/* ── Riftbound ───────────────────────────────────────────────── */

export const RIFTBOUND_HAND_RULES: GameHandRules = {
  handSize: 4,
  mulliganType: "partial",
  maxMulligans: 1,
  partialMulliganMaxReturn: 2,
  drawButtons: [
    { count: 1, label: "Draw 1", shortcutKey: " ", shortcutLabel: "Space" },
    { count: 2, label: "Draw 2", shortcutKey: "2", shortcutLabel: "2" },
    { count: 3, label: "Draw 3", shortcutKey: "3", shortcutLabel: "3" },
  ],
  excludeTypes: RIFTBOUND_NON_MAIN_TYPES,
  labels: {
    mulliganButton: "Partial Mulligan",
    shortcutHint:
      "Click cards to remove \u2022 Space = draw \u2022 R = new hand \u2022 ESC = close",
    costCurveTitle: "Cost curve",
    costCurveSubtitle: "Action cards only.",
  },
  mainDrawPerTurn: 1,
  secondaryPool: {
    label: "Runes",
    includeTypes: ["Rune"],
    drawPerTurn: 2,
  },
  getVerdict: (hand) => {
    const earlyPlays = countByCostAtMost(hand, 2);
    if (earlyPlays === 0)
      return {
        label: "No early plays \u2014 risky",
        className: "text-warning",
      };
    if (earlyPlays >= 3)
      return { label: "Strong start", className: "text-primary" };
    if (earlyPlays >= 1)
      return { label: "Playable", className: "text-primary" };
    return { label: "Playable", className: "text-base-content/70" };
  },
  resourceStat: {
    label: "Early plays",
    count: (hand) => countByCostAtMost(hand, 2),
    goodRange: [1, 4],
  },
  hypergeomPresets: [
    {
      id: "early1",
      label: "At least 1 early play",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 1,
      rel: "gte",
    },
    {
      id: "early2",
      label: "At least 2 early plays",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 2,
      rel: "gte",
    },
    {
      id: "no-early",
      label: "No early plays",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 0,
      rel: "eq",
    },
  ],
};

/* ── One Piece ───────────────────────────────────────────────── */

export const ONE_PIECE_HAND_RULES: GameHandRules = {
  handSize: 5,
  mulliganType: "full",
  maxMulligans: 1,
  partialMulliganMaxReturn: 0,
  drawButtons: [
    { count: 1, label: "Draw 1", shortcutKey: " ", shortcutLabel: "Space" },
    { count: 2, label: "Draw 2", shortcutKey: "2", shortcutLabel: "2" },
    { count: 3, label: "Draw 3", shortcutKey: "3", shortcutLabel: "3" },
  ],
  // Belt-and-braces: Leader/DON!! live outside the mainboard already.
  excludeTypes: ["Leader", "DON!!"],
  labels: {
    mulliganButton: "Mulligan",
    shortcutHint:
      "Click cards to remove • Space = draw • R = new hand • ESC = close",
    costCurveTitle: "Cost curve",
    costCurveSubtitle: "Character & event cards.",
  },
  mainDrawPerTurn: 1,
  getVerdict: (hand) => {
    const earlyPlays = countByCostAtMost(hand, 2);
    if (earlyPlays === 0)
      return {
        label: "No early plays — risky",
        className: "text-warning",
      };
    if (earlyPlays >= 3)
      return { label: "Strong start", className: "text-primary" };
    return { label: "Playable", className: "text-primary" };
  },
  resourceStat: {
    label: "Early plays",
    count: (hand) => countByCostAtMost(hand, 2),
    goodRange: [1, 4],
  },
  hypergeomPresets: [
    {
      id: "early1",
      label: "At least 1 early play",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 1,
      rel: "gte",
    },
    {
      id: "early2",
      label: "At least 2 early plays",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 2,
      rel: "gte",
    },
    {
      id: "no-early",
      label: "No early plays",
      countK: (mb) => sumByCostAtMost(mb, 2),
      want: 0,
      rel: "eq",
    },
  ],
};
